import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subject, Subscription, combineLatest, merge } from 'rxjs';
import { debounceTime, finalize, map, startWith, takeUntil } from 'rxjs/operators';
import { BorrowerService } from '@services/borrower.service';
import { BankSettingsService } from '@services/bank-settings.service';
import { Company } from '@models/company';
import { TransferErrorView } from '@services/transfer-borrower.types';

@Component({
  selector: 'app-transfer-target-search',
  templateUrl: './transfer-target-search.component.html',
  styleUrls: ['./transfer-target-search.component.scss'],
  standalone: false,
})
export class TransferTargetSearchComponent implements OnInit, OnDestroy {
  /** UUID of the source borrower — excluded from results client-side. */
  @Input() sourceUuid!: string;

  /** View-model for the inline error band. Parent owns the source of truth. */
  @Input() error: TransferErrorView | null = null;

  /** Emits the company the user selected via the radio button, or null on deselect. */
  @Output() whenSelected = new EventEmitter<Company | null>();

  nameControl = new FormControl<string>('', { nonNullable: true });
  gfcidControl = new FormControl<string>('', { nonNullable: true });

  searchResults: Company[] = [];
  searchInProgress = false;
  noResultsMessageIsVisible = false;
  selectedUuid: string | null = null;
  selectedCompany: Company | null = null;

  page = 1;
  readonly limit = 10;
  total = 0;
  readonly pagerId = 'tts-pager';

  readonly emptyStateIcon = 'fa-file-circle-question';
  readonly emptyStateHeader = 'No results found';
  readonly emptyStateHelp =
    "We couldn't find a match for that criteria. Please try another search or add a new borrower.";
  contactSupportEmail = '';
  customerSupportUrl = '';

  private destroy$ = new Subject<boolean>();
  private searchSubscription?: Subscription;

  constructor(
    private borrowerService: BorrowerService,
    private bankSettingsService: BankSettingsService,
  ) {}

  ngOnInit(): void {
    merge(
      this.nameControl.valueChanges,
      this.gfcidControl.valueChanges,
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this._clearSelection());

    combineLatest([
      this.nameControl.valueChanges.pipe(startWith('')),
      this.gfcidControl.valueChanges.pipe(startWith('')),
    ])
      .pipe(debounceTime(400), takeUntil(this.destroy$))
      .subscribe(([name, gfcid]) => {
        // Filter inputs changed → reset to page 1.
        this.page = 1;
        this.runSearch(name, gfcid);
      });
    this.bankSettingsService.getPortalSettings()
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: any) => {
        const portal = data?.['_portalSettings'] || {};
        this.contactSupportEmail = portal['companySearchEmptyStateContactEmail'] || '';
        this.customerSupportUrl  = portal['customerSupportUrl'] || '';
      });
  }

  openSupportEmail(): void {
    if (this.contactSupportEmail) {
      window.open(`mailto:${this.contactSupportEmail}`);
    }
  }

  changePage(page: number): void {
    this.page = page;
    this.runSearch(this.nameControl.value, this.gfcidControl.value);
  }

  runSearch(name: string, gfcid: string): void {
    this.noResultsMessageIsVisible = false;
    const trimmedName = (name || '').trim();
    const trimmedGfcid = (gfcid || '').trim();

    this.searchInProgress = true;
    this.searchSubscription?.unsubscribe();
    this.searchSubscription = this.borrowerService
      .searchOnboardedBorrowersWithCount({
        name: trimmedName,
        gfcid: trimmedGfcid,
        page: this.page,
        limit: this.limit,
      })
      .pipe(
        takeUntil(this.destroy$),
        map(result => ({
          objects: result.objects.filter(c => c.uuid !== this.sourceUuid),
          total: result.total,
        })),
        finalize(() => {
          this.searchInProgress = false;
        }),
      )
      .subscribe(result => {
        this.searchResults = result.objects;
        this.total = result.total;
        this.noResultsMessageIsVisible = result.objects.length === 0;
      });
  }

  clearSearch(): void {
    this.nameControl.setValue('');
    this.gfcidControl.setValue('');
    this.searchResults = [];
    this.total = 0;
    this.page = 1;
    this.noResultsMessageIsVisible = false;
    this._clearSelection();
  }

  private _clearSelection(): void {
    if (this.selectedUuid === null) return;
    this.selectedUuid = null;
    this.selectedCompany = null;
    this.whenSelected.emit(null);
  }

  selectRow(company: Company): void {
    const clickedUuid = company.uuid ?? null;
    if (this.selectedUuid !== null && this.selectedUuid === clickedUuid) {
      this.selectedUuid = null;
      this.selectedCompany = null;
      this.whenSelected.emit(null);
      return;
    }
    this.selectedUuid = clickedUuid;
    this.selectedCompany = company;
    this.whenSelected.emit(company);
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
